import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import '../styles/preview.css';
import axios from 'axios';
import config from '../config';
import { useCart } from "./CartContext"
import GroupedModal from "./GroupedModal.jsx";

import 'react-image-crop/dist/ReactCrop.css'

import { pdfjs } from "react-pdf";

const host = config.backendUrl;

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function Preview({title}) {
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [pdf, setPdf] = useState(null);
    const [pdfSpecs, setPdfSpecs] = useState(null);
    const [choices, setChoices] = useState({});
    const [error, setError] = useState(null);
    const [openModal, setOpenModal] = useState(null);
    const [checkoutDisabled, setCheckoutDisabled] = useState(true);

    const [groupedModals, setGroupedModals] = useState({});

    const { addItem } = useCart();

    const effectRan = useRef(false);

    useEffect(() => {
        document.title = title;
        if (effectRan.current === false) {
            fetchProductData();
            effectRan.current = true;
        }
    }, [productId]);

    const openDB = () => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open("GFUserDB", 1);
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains("products")) {
                    db.createObjectStore("products", { keyPath: "key" });
                }
                if (!db.objectStoreNames.contains("items")) {
                    db.createObjectStore("items", { keyPath: "key" });
                }
                };
            request.onsuccess = (event) => {
                resolve(event.target.result);
            };
            request.onerror = (event) => {
                reject("Failed to open IndexedDB");
            };
        });
    };
    
    const saveToIndexedDB = async (key, value) => {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction("products", "readwrite");
            const store = transaction.objectStore("products");
            const request = store.put({ key, value });
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject("Failed to save data to IndexedDB");
        });
    };
    
    const getFromIndexedDB = async (key) => {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction("products", "readonly");
            const store = transaction.objectStore("products");
            const request = store.get(key);
            request.onsuccess = () => resolve(request.result?.value || null);
            request.onerror = () => reject("Failed to retrieve data from IndexedDB");
        });
    };

    const fetchProductData = () => {
        fetch(`${host}/api/products/${productId}`,
            {
                method: "GET",
                headers: {
                    "X-API-Key": process.env.REACT_APP_GANDF_KEY
                }
            }
        )
            .then((response) => response.json())
            .then((data) => {
                setProduct(data);
                const modals = data.personalizations.filter((p) => p.type === 'modal');

                if (modals.length > 0) {
                    // Group modals by page number
                    const grouped = modals.reduce((acc, modal) => {
                        const page = modal.page;
                        if (!acc[page]) {
                            acc[page] = [];
                        }
                        acc[page].push(modal);
                        return acc;
                    }, {});
                    setGroupedModals(grouped);
                }
            })
            .catch((error) => {
                console.error('Error fetching product:', error);
                setError('Failed to fetch product details');
            });

        // if pdf is in indexedDB, fetch it
        getFromIndexedDB(`${productId}`)
            .then(async (db_pdf) => {
                console.log(db_pdf);
                if (db_pdf.pdf) {
                    // check if expiry date is passed
                    const expiryDate = new Date(db_pdf.pdf.expiry * 1000);
                    const currentDate = new Date();
                               
                    if (currentDate < expiryDate) {
                        setPdf(db_pdf.pdf.img_urls);
                        setPdfSpecs({
                            ...db_pdf.pdf,
                            img_urls: undefined
                        });

                        const choices = await getFromIndexedDB(`${productId}`) || {};
                        setChoices(choices);

                    } else {
                        // remove pdf from indexedDB
                        await saveToIndexedDB(productId, {
                            ...db_pdf,
                            pdf: undefined,
                            folder_id: undefined,
                            item_id: undefined
                        });
                        console.log("Fetching new")
                        fetchPdfPreview();
                    }

                } else {
                    fetchPdfPreview();
                }
            })
            .catch((error) => {
                console.error('Error fetching PDF from IndexedDB:', error);
                fetchPdfPreview();
            });
    };

    const fetchPdfPreview = async () => {
        try {
            const choices = await getFromIndexedDB(productId);

            if (!choices) {
                window.location.href = `/books/${productId}/personalize`;
            }

            setChoices(choices);
    
            const response = await axios.post(
                `${host}/api/products/${productId}/preview`,
                choices,
                { headers: { "Content-Type": "application/json",
                                "X-API-Key": process.env.REACT_APP_GANDF_KEY
                 } }
            );
    
            const imgURLS = response.data.img_urls;
            const newPdf = {
                img_urls: imgURLS,
                expiry: response.data.expiry,
            }

            setChoices((prevChoices) => ({
                ...prevChoices,
                item_id: response.data.item_id,
                folder_id: response.data.folder_id,
                recipient: response.data.recipient,
                bookType: response.data.bookType,
                pdf: newPdf
            }));

            await saveToIndexedDB(productId, {
                ...choices,
                item_id: response.data.item_id,
                folder_id: response.data.folder_id,
                recipient: response.data.recipient,
                bookType: response.data.bookType,
                pdf: newPdf
            });

            setPdf(imgURLS);
            setPdfSpecs({
                ...response.data,
                img_urls: undefined
            });

        } catch (error) {
            console.error("Error fetching PDF preview:", error);
            setError("Failed to fetch PDF preview");
        }
    };    

    const handleOpenModal = (modal) => {
        setOpenModal(modal);
    };

    const handleCloseModal = () => {
        setOpenModal(null);
    };

    const handleSaveGroupedModal = async (inputs) => {
        try {
            const existingChoices = await getFromIndexedDB(productId) || {};
            
            // Separate files from regular inputs BEFORE any IndexedDB operations
            const regularInputs = {};
            const fileInputs = {};
            
            for (const [key, value] of Object.entries(inputs)) {
                if (value instanceof File) {
                    fileInputs[key] = value;
                    // Don't include files in regularInputs yet
                } else {
                    regularInputs[key] = value;
                }
            }
            
            const updatedChoices = {
                ...existingChoices,
                ...regularInputs, // Only non-file inputs
                folder_id: choices.folder_id,
                page_num: openModal[0]?.page
            };
    
            // Handle file uploads first
            let uploadedPaths = {};
            const hasFiles = Object.keys(fileInputs).length > 0;
    
            if (hasFiles) {
                console.log("Uploading files...");
                const formData = new FormData();
                
                // Use the original File objects from inputs, not from IndexedDB
                for (const [key, file] of Object.entries(fileInputs)) {
                    formData.append(key, file);
                }
                
                formData.append("folder_id", updatedChoices.folder_id);
    
                const saveUploads = await axios({
                    method: "post",
                    url: `${host}/api/products/${productId}/save-user-uploads`,
                    data: formData,
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "X-API-Key": process.env.REACT_APP_GANDF_KEY
                    }
                });
    
                if (saveUploads.status !== 200) {
                    throw new Error("Failed to save uploads");
                }
    
                uploadedPaths = saveUploads.data.paths;
            }
    
            // Now combine regular inputs with uploaded file paths
            const finalChoices = {
                ...updatedChoices,
                ...uploadedPaths  // Add server paths for uploaded files
            };
    
            // Save to IndexedDB only after files are uploaded and we have server paths
            await saveToIndexedDB(productId, finalChoices);
                
            const response = await axios({
                method: "post",
                url: `${host}/api/products/${productId}/preview-page`,
                data: finalChoices,
                headers: {
                    "Content-Type": "application/json",
                    "X-API-Key": process.env.REACT_APP_GANDF_KEY
                }
            });
        
            // Update PDF display
            setPdf((prevPdf) => {
                const newPdf = [
                    ...prevPdf.slice(0, openModal[0].page + 1),
                    response.data.img_url,
                    ...prevPdf.slice(openModal[0].page + 2)
                ];
                return newPdf;
            });
    
            // Update IndexedDB with new PDF
            await saveToIndexedDB(productId, {
                ...finalChoices,
                pdf: {
                    expiry: pdfSpecs.expiry,
                    img_urls: [
                        ...pdf.slice(0, openModal[0].page + 1),
                        response.data.img_url,
                        ...pdf.slice(openModal[0].page + 2)
                    ]
                }
            });
    
            setChoices(finalChoices);
            handleCloseModal();
    
        } catch (error) {
            console.error("Error saving modal data:", error);
            setError(error.response?.data?.message || "Failed to save modal data");
        }
    };

    const getValidModalsForPage = (pageModals) => {
        if (!pageModals) return [];

        return pageModals.filter(modal => 
            modal.condition === undefined || 
            modal.condition.values.includes(choices[modal.condition.key])
        );
    };

    const addToCart = () => {
        console.log(choices);
        addItem({
            name: product.name,
            description: product.description,
            price: product.price,
            currency: "USD",
            id: choices.item_id,
            choices: choices,
            image: pdf[0],
            img_urls: choices.pdf.img_urls,
            readable_id: productId,
            product_id: product.product_id,
            item_id: choices.item_id,
            price_id: product.price_id
        },
        {
            recipient: choices.recipient,
            bookType: choices.bookType
        }
    );
        window.location.href = window.location.origin + "/cart";
    }

    const checkDisclaimer = () => {
        const disclaimerCheckboxes = document.querySelectorAll('.disclaimer-checkbox');
        const allChecked = Array.from(disclaimerCheckboxes).every(checkbox => checkbox.checked);
        const checkoutButton = document.querySelector('.purchase-link');

        if (allChecked) {
            setCheckoutDisabled(false);
        } else {
            setCheckoutDisabled(true);
        }
    }

    

    if (error) {
        return (
            <div className="error-container">
                <h2 className="error-title">Something didn't go as planned while creating your preview.</h2>
                <p className="error-message">If this continues, please send us a note: <a href="mailto:hello@grieversandfriends.com">hello@grieversandfriends.com</a>.</p>
                <p className="error-message">Thank you for your patience.</p>
                <Link to={`/books/${productId}/personalize`} className="error-link">Back to Personalization</Link>
            </div>
        )
    }

    return (
        <div className="preview">
            {pdf && product && (
                <>

                    <div className="preview-header">
                        <span className="preview-title">Preview Your Personalized Book</span>
                        <span className="preview-description">You’re almost there—this is how your book will appear in print. Scroll through to make sure everything looks just right. Optional “Customize Page” buttons are included for extra special touches. You can skip them if you’d like—your book is already filled with love.</span>
                    </div>

                    <div className="flipbook-container">
                        <div className="cover-container">
                            <img src={pdf[0] || ''} alt="page 1" width={1000} height={500} className="cover-image" />
                            <div className="cover-spine"></div>
                        </div>
                        {Array.from(new Array(Math.floor(product.numPages / 2) + (product.numPages % 2)), (el, index) => (
                            <div key={index} className="full-page">
                                <div className="page-pair">
                                <div className="page-container">
                                    <div className="page-content left">
                                        <img
                                            key={`page_${index * 2 + 2}`}
                                            src={pdf[index * 2 + 1] || ''}
                                            alt={`page ${index * 2 + 2}`}
                                            width={500}
                                            height={500}
                                            id={`page_${index * 2 + 2}`}
                                            className={`pdf-page left ${(index * 2 + 2 === 2) ? 'blank' : ''}`}
                                        />
                                    </div>
                                    {(() => {
                                            const validModals = getValidModalsForPage(groupedModals[index * 2]);
                                            return validModals.length > 0 ? (
                                                <div className="modal-button-container">
                                                    <button 
                                                        className="modal-button"
                                                        onClick={() => handleOpenModal(validModals)}
                                                    >
                                                        {validModals.length > 1 ? "Customize this page" : validModals[0].label}
                                                    </button>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                    <div className="center-fold">
                                        <div className="fold-gradient" />
                                    </div>
                                    <div className="page-container">
                                        <div className="page-content right">
                                            <img
                                                key={`page_${index * 2 + 3}`}
                                                src={pdf[index * 2 + 2] || ''}
                                                alt={`page ${index * 2 + 3}`}
                                                width={500}
                                                height={500}
                                                id={`page_${index * 2 + 3}`}
                                                className={`pdf-page right ${(index * 2 + 3 == product.numPages + 2) ? 'blank' : ''}`}
                                            />
                                        </div>
                                        {(() => {
                                            const validModals = getValidModalsForPage(groupedModals[index * 2 + 1]);
                                            return validModals.length > 0 ? (
                                                <div className="modal-button-container">
                                                    <button 
                                                        className="modal-button"
                                                        onClick={() => handleOpenModal(validModals)}
                                                    >
                                                        {validModals.length > 1 ? "Customize this page" : validModals[0].label}
                                                    </button>
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="purchase-container">
                            <div className="disclaimer">
                                <input type="checkbox" id="disclaimer-1" className="disclaimer-checkbox" onChange={checkDisclaimer} />
                                <label for="disclaimer-1" className="disclaimer-text">I have reviewed all customizations and approve it for printing as displayed. I&nbsp;understand no further edits can be made after purchase.</label>
                            </div>
                        <button onClick={addToCart} disabled={checkoutDisabled} className="purchase-link">Add to Cart</button>
                    </div>

                </>
            )}

            {!pdf && (
                <div className="flipbook-container">
                    <div className="loader"></div>
                    <p className="loading-text">Your book is loading with love…</p>
                </div>
            )}

            {openModal && (
                <GroupedModal 
                    isOpen={true}
                    modals={Array.isArray(openModal) ? openModal : [openModal]}
                    choices={choices}
                    onModalClose={handleCloseModal}
                    onSave={handleSaveGroupedModal}
                />
            )}
        </div>
    );
}
